import React, { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { SortingState, ColumnFiltersState } from "@tanstack/react-table";
import { toast } from "sonner";
import { Plus, Loader2, AlertTriangle, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useAuthStore } from "@/app/store/authStore";
import { Link } from "react-router-dom";
import { PaginatedResponse, ListQueryParams } from "@/app/types/api";
import { TaxRate, TaxRateCreate, TaxRateUpdate } from "@/app/types/taxRate";
import { TaxComponent } from "@/app/types/taxComponent";
import { listTaxRates, createTaxRate, updateTaxRate, deleteTaxRate } from "@/app/services/taxRateService";
import { listTaxComponents } from "@/app/services/taxComponentService";
import TaxRateDataTable from "./components/TaxRateDataTable";
import TaxRateForm from "./components/TaxRateForm";
import TaxComponentManagement from "./components/TaxComponentManagement";
import { columns as taxRateColumns } from "./components/columns";
import { AuthState } from "@/app/store/authStore";
import { User } from "@/app/types/auth";

// Define selectors for auth store
const selectUser = (state: AuthState): User | null => state.user;
const selectLogoutAction = (state: AuthState): (() => void) => state.logoutAction;
const selectIsLoading = (state: AuthState): boolean => state.isLoading;

const TaxRatesPage: React.FC = () => {
  const queryClient = useQueryClient();
  
  // Local State
  const [isCreateFormOpen, setIsCreateFormOpen] = useState(false);
  const [isEditFormOpen, setIsEditFormOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isComponentManagerOpen, setIsComponentManagerOpen] = useState(false);
  const [currentTaxRate, setCurrentTaxRate] = useState<TaxRate | null>(null);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [searchValue, setSearchValue] = useState("");
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
    pageCount: 0,
  });
  
  // Auth state
  const user = useAuthStore(selectUser);
  const logoutAction = useAuthStore(selectLogoutAction);
  const isAuthLoading = useAuthStore(selectIsLoading);
  
  // Get organization ID from user data
  const userOrgId = useMemo(() => user?.organization_id || user?.organization?.id, [user]);
  const isOrgAdmin = useMemo(() => user?.role?.name === 'org_admin', [user]);
  
  // Construct query parameters for the API
  const queryParams = useMemo<ListQueryParams>(() => {
    const params: ListQueryParams = {
      skip: pagination.pageIndex * pagination.pageSize,
      limit: pagination.pageSize,
      search_query: searchValue || undefined,
    };
    
    // Add sorting if defined
    if (sorting.length > 0) {
      params.sort_by = sorting[0].id;
      params.sort_dir = sorting[0].desc ? "desc" : "asc";
    }
    
    // Add filters if defined
    if (columnFilters.length > 0) {
      const filters: { [key: string]: any } = {};
      columnFilters.forEach((filter) => {
        filters[filter.id] = filter.value;
      });
      params.filters = JSON.stringify(filters);
    }
    
    return params;
  }, [pagination.pageIndex, pagination.pageSize, searchValue, sorting, columnFilters]);
  
  // Query to fetch tax rates
  const { 
    data: taxRatesResponse = { items: [], total: 0 },
    isLoading: isTaxRatesLoading, 
    isError: isTaxRatesError,
    error: taxRatesError,
    refetch: refetchTaxRates,
  } = useQuery<PaginatedResponse<TaxRate>>({
    queryKey: ['taxrates', userOrgId, queryParams],
    queryFn: () => {
      if (!userOrgId) {
        throw new Error('No organization ID available');
      }
      return listTaxRates(queryParams, userOrgId);
    },
    enabled: !!userOrgId,
  });

  // Query to fetch tax components (for the dropdown)
  const {
    data: componentsResponse,
    isLoading: isComponentsLoading,
    isError: isComponentsError,
    error: componentsError,
  } = useQuery<PaginatedResponse<TaxComponent>>({
    queryKey: ['taxComponents', userOrgId, 'all'],
    queryFn: () => {
      if (!userOrgId) {
        throw new Error('No organization ID available');
      }
      const params: ListQueryParams = { skip: 0, limit: 500, sort_by: 'name', sort_dir: 'asc' };
      return listTaxComponents(params, userOrgId);
    },
    enabled: !!userOrgId,
    staleTime: 5 * 60 * 1000, // Cache components for 5 mins
  });

  const availableComponents = componentsResponse?.items ?? [];
  
  // Update pagination when total changes
  useEffect(() => {
    if (taxRatesResponse) {
      setPagination(prev => ({
        ...prev,
        pageCount: Math.ceil(taxRatesResponse.total / prev.pageSize),
      }));
    }
  }, [taxRatesResponse]);

  // Setup event listeners for edit/delete actions from the table
  useEffect(() => {
    const handleEditTaxRate = (event: CustomEvent<TaxRate>): void => {
      setCurrentTaxRate(event.detail);
      setIsEditFormOpen(true);
    };

    const handleDeleteTaxRate = (event: CustomEvent<TaxRate>): void => {
      setCurrentTaxRate(event.detail);
      setIsDeleteDialogOpen(true);
    };

    document.addEventListener('edit-taxrate', handleEditTaxRate as EventListener);
    document.addEventListener('delete-taxrate', handleDeleteTaxRate as EventListener);

    return () => {
      document.removeEventListener('edit-taxrate', handleEditTaxRate as EventListener);
      document.removeEventListener('delete-taxrate', handleDeleteTaxRate as EventListener);
    };
  }, []);
  
  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: TaxRateCreate) => createTaxRate(data, userOrgId!),
    onSuccess: () => {
      toast.success('Tax rate created successfully');
      queryClient.invalidateQueries({ queryKey: ['taxrates', userOrgId] });
      setIsCreateFormOpen(false);
    },
    onError: (error) => {
      toast.error(`Failed to create tax rate: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: TaxRateUpdate }) => updateTaxRate(id, data),
    onSuccess: () => {
      toast.success('Tax rate updated successfully');
      queryClient.invalidateQueries({ queryKey: ['taxrates', userOrgId] });
      setIsEditFormOpen(false);
      setCurrentTaxRate(null);
    },
    onError: (error) => {
      toast.error(`Failed to update tax rate: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteTaxRate(id),
    onSuccess: () => {
      toast.success('Tax rate deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['taxrates', userOrgId] });
      setIsDeleteDialogOpen(false);
      setCurrentTaxRate(null);
    },
    onError: (error) => {
      toast.error(`Failed to delete tax rate: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  });
  
  // Event Handlers
  const handleCreateClick = (): void => {
    setIsCreateFormOpen(true);
  };

  const handleCreateSubmit = (formData: TaxRateCreate): void => {
    createMutation.mutate(formData);
  };

  const handleEditSubmit = (formData: TaxRateUpdate): void => {
    if (currentTaxRate) {
      updateMutation.mutate({ id: currentTaxRate.id, data: formData });
    }
  };

  const handleDeleteConfirm = (): void => {
    if (currentTaxRate) {
      deleteMutation.mutate(currentTaxRate.id);
    }
  };
  
  const handlePaginationChange = (newPagination: { pageIndex: number; pageSize: number }): void => {
    setPagination(prev => ({
      ...prev,
      pageIndex: newPagination.pageIndex,
      pageSize: newPagination.pageSize,
    }));
  };
  
  // --- Render Logic ---
  
  // Show loading indicator if auth state is loading
  if (isAuthLoading) {
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
      </div>
    );
  }
  
  // No organization assigned
  if (!isAuthLoading && !userOrgId) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] space-y-4 text-center p-8">
        <AlertTriangle className="h-16 w-16 text-amber-500" />
        <h2 className="text-2xl font-semibold">No Organization Assigned</h2>
        <p className="max-w-md text-muted-foreground">
          You need to be assigned to an organization to manage tax rates.
          Please contact your administrator.
        </p>
        <div className="flex gap-4 mt-4">
          <Link to="/dashboard">
            <Button variant="outline">Go to Dashboard</Button>
          </Link>
          <Button onClick={logoutAction} variant="destructive">
            Logout
          </Button>
        </div>
      </div>
    );
  }
  
  // Handle errors (either tax rates or components)
  const isError = isTaxRatesError || isComponentsError;
  const error = taxRatesError || componentsError;
  const isLoading = isTaxRatesLoading || isComponentsLoading;

  if (isError && !!userOrgId) {
    return (
      <div className="py-8 flex flex-col items-center justify-center space-y-4 text-center">
        <AlertTriangle className="h-12 w-12 text-destructive" />
        <p className="text-xl font-semibold text-destructive">Error Loading Data</p>
        <p className="text-sm text-muted-foreground">
          {error instanceof Error ? error.message : 'An unknown error occurred while loading tax rates or components.'}
        </p>
        <Button 
          onClick={() => { 
            refetchTaxRates(); 
            queryClient.refetchQueries({ queryKey: ['taxComponents', userOrgId, 'all'] }); 
          }} 
          variant="outline"
        >
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Retry
        </Button>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold tracking-tight">Tax Rates</h1>
        <div className="flex gap-2">
          {/* Button to open Tax Component Manager */}
          <Dialog open={isComponentManagerOpen} onOpenChange={setIsComponentManagerOpen}>
            <DialogTrigger asChild>
              <Button variant="outline">
                <Settings className="mr-2 h-4 w-4" /> Manage Components
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-2xl max-h-[90vh]">
              <DialogHeader>
                <DialogTitle>Tax Component Management</DialogTitle>
                <DialogDescription>Add, edit, or remove individual tax components used in your rates.</DialogDescription>
              </DialogHeader>
              <ScrollArea className="max-h-[70vh] p-1">
                <TaxComponentManagement />
              </ScrollArea>
            </DialogContent>
          </Dialog>

          {isOrgAdmin && (
            <Button onClick={handleCreateClick}>
              <Plus className="mr-2 h-4 w-4" /> Add Tax Rate
            </Button>
          )}
        </div>
      </div>
      <p className="text-muted-foreground">
        Manage tax rates (e.g., GST 18%) and their component breakdown (e.g., CGST 9% + SGST 9%).
      </p>
      
      {/* Tax Rates Data Table */}
      <TaxRateDataTable
        data={taxRatesResponse}
        columns={taxRateColumns}
        isLoading={isLoading}
        isOrgAdmin={isOrgAdmin}
        pagination={pagination}
        pageCount={pagination.pageCount}
        sorting={sorting}
        columnFilters={columnFilters}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        onPaginationChange={handlePaginationChange}
        onSortingChange={setSorting}
        onColumnFiltersChange={setColumnFilters}
      />
      
      {/* Create Form */}
      <TaxRateForm
        open={isCreateFormOpen}
        onOpenChange={setIsCreateFormOpen}
        onSubmit={handleCreateSubmit}
        isLoading={createMutation.isPending}
        mode="create"
        availableComponents={availableComponents}
      />
      
      {/* Edit Form */}
      {currentTaxRate && (
        <TaxRateForm
          open={isEditFormOpen}
          onOpenChange={(open) => {
            setIsEditFormOpen(open);
            if (!open) setCurrentTaxRate(null);
          }}
          onSubmit={handleEditSubmit}
          defaultValues={currentTaxRate}
          isLoading={updateMutation.isPending}
          mode="edit"
          availableComponents={availableComponents}
        />
      )}
      
      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the tax rate &quot;{currentTaxRate?.name}&quot;.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteConfirm} 
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TaxRatesPage; 